import { useEffect, useMemo, useState } from 'react'
import type { SavedFilterDocument } from '@/lib/db/rxdb/hooks'
import type { FilterFieldKey } from '@/lib/types/filter'
import type {SavedFilterScope} from '@/lib/types/savedFilter';
import { useSavedFilters } from '@/lib/db/rxdb/hooks'
import useProjectFilterStore from '@/store/ProjectFilterStore'
import useProjectViewPreferencesStore from '@/store/ProjectViewPreferencesStore'
import useLoginStore from '@/store/LoginStore'
import {
  
  parseSavedFilterConfig,
  parseSavedLayoutConfig
} from '@/lib/types/savedFilter'

export type HydrationStatus = 'pending' | 'ready' | 'missing' | 'forbidden'

interface UseViewHydratorParams {
  viewId: string | undefined
  /** The scope entity (space or project) id the current URL resolves to. */
  scopeEntityId: string | undefined
  scope: SavedFilterScope
  pinnedFields?: Array<FilterFieldKey>
}

interface UseViewHydratorResult {
  status: HydrationStatus
  view: SavedFilterDocument | null
}

/**
 * Looks up a saved view by id, verifies the caller may see it, and hydrates
 * the filter + layout stores so the surrounding page renders as if the user
 * had manually reproduced the saved state.
 */
export function useViewHydrator({
  viewId,
  scopeEntityId,
  scope,
  pinnedFields,
}: UseViewHydratorParams): UseViewHydratorResult {
  const userId = useLoginStore((s) => s.userId)
  const { savedFilters, isLoading } = useSavedFilters()
  const loadSavedFilterMerged = useProjectFilterStore((s) => s.loadSavedFilterMerged)
  const loadSavedFilter = useProjectFilterStore((s) => s.loadSavedFilter)
  const applyPreferences = useProjectViewPreferencesStore((s) => s.applyPreferences)

  const [hasHydrated, setHasHydrated] = useState(false)

  const view = useMemo<SavedFilterDocument | null>(() => {
    if (!viewId || !savedFilters) return null
    return savedFilters.find((f) => f.id === viewId && !f.isDeleted) ?? null
  }, [savedFilters, viewId])

  const status: HydrationStatus = useMemo(() => {
    if (!viewId || !scopeEntityId) return 'pending'
    if (isLoading) return 'pending'
    if (!view) return 'missing'
    if (view.scope !== scope || view.scopeEntityId !== scopeEntityId) return 'missing'
    if (view.permission === 'private' && view.ownerId !== userId) return 'forbidden'
    if (!hasHydrated) return 'pending'
    return 'ready'
  }, [viewId, scopeEntityId, isLoading, view, scope, userId, hasHydrated])

  useEffect(() => {
    if (!view || !scopeEntityId) return
    if (view.scope !== scope || view.scopeEntityId !== scopeEntityId) return
    if (view.permission === 'private' && view.ownerId !== userId) return

    const filters = parseSavedFilterConfig(view.filterConfig)
    if (pinnedFields) {
      loadSavedFilterMerged(scopeEntityId, filters, pinnedFields)
    } else {
      loadSavedFilter(scopeEntityId, filters)
    }

    const layout = parseSavedLayoutConfig(view.layoutConfig)
    if (layout) {
      applyPreferences(scopeEntityId, layout)
    }

    setHasHydrated(true)
  }, [
    view,
    scopeEntityId,
    scope,
    userId,
    pinnedFields,
    loadSavedFilter,
    loadSavedFilterMerged,
    applyPreferences,
  ])

  // Reset hydration state when the target view changes so the next one applies.
  useEffect(() => {
    setHasHydrated(false)
  }, [viewId])

  return { status, view }
}
